/**
 * @fileoverview Acciones optimizadas para actualizar o insertar conversaciones en el inbox.
 * Proporciona acciones síncronas y asíncronas para la gestión eficiente del estado de conversaciones.
 * @module actions/inbox/conversationOptimizedActions
 * @category Actions Layer
 * @since 2.0.0
 * @version 2.1.0
 */

/**
 * Tipo de acción para actualizar o agregar una conversación en el store.
 * @constant
 * @type {string}
 * @default
 * @since 2.0.0
 */
export const UPDATE_OR_ADD_CONVERSATION = 'UPDATE_OR_ADD_CONVERSATION';

/**
 * Acción síncrona para actualizar o insertar una conversación en el store.
 *
 * @function updateOrInsertConversationSync
 * @category Action - Síncrona
 * @memberof module:actions/inbox/conversationOptimizedActions
 *
 * @param {Object} processedData - Datos procesados de la conversación.
 * @param {string|number} processedData.id - ID único de la conversación.
 * @param {Object} processedData.changes - Cambios a aplicar en la conversación.
 * @param {boolean} [processedData.isUpsert=false] - Indica si es una inserción (upsert).
 *
 * @returns {Object} Acción redux con tipo y payload.
 *
 * @example
 * dispatch(updateOrInsertConversationSync({ id: 123, changes: { status: 'open' }, isUpsert: true }));
 */
export function updateOrInsertConversationSync(processedData) {
  return {
    type: UPDATE_OR_ADD_CONVERSATION,
    payload: processedData
  };
}

/**
 * Acción asíncrona optimizada para actualizar o insertar una conversación.
 *
 * Implementa el patrón de Actions Layer para la gestión eficiente de conversaciones en el inbox.
 * Utiliza una acción síncrona interna para actualizar el store y retorna el resultado de la operación.
 *
 * @function updateOrInsertConversationOptimized
 * @category Action - Asíncrona
 * @memberof module:actions/inbox/conversationOptimizedActions
 *
 * @param {Object} conversationData - Datos de la conversación a procesar.
 * @param {string|number} conversationData.id - ID único de la conversación.
 * @param {Object} conversationData.changes - Cambios a aplicar en la conversación.
 * @param {boolean} [conversationData.isUpsert=false] - Indica si es una inserción (upsert).
 *
 * @returns {Function} Función thunk que despacha la acción y retorna un objeto de resultado.
 * @example
 * dispatch(updateOrInsertConversationOptimized({ id: 123, changes: { status: 'open' }, isUpsert: true }));
 *
 * @since 2.0.0
 * @version 2.1.0 - Optimización de validaciones y retorno de resultado
 */
export const updateOrInsertConversationOptimized = (conversationData) => async (dispatch, getState) => {
  const {
    id, changes, isUpsert = false, conversationListType = null
  } = conversationData;
  const hasValidChanges = Boolean(changes) && typeof changes === 'object' && !Array.isArray(changes) && Object.keys(changes).length > 0;

  if (!id || !hasValidChanges) {
    return { success: false };
  }

  const state = getState();
  const currentRetailerUser = state.mainReducer?.currentRetailerUser;
  const currentUserId = state.mainReducer?.currentRetailerUser?.id;

  dispatch(updateOrInsertConversationSync({
    id,
    changes,
    isUpsert,
    currentUserId,
    currentRetailerUser,
    conversationListType
  }));

  return { success: true };
};
